import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Button, CheckboxDropdown } from 'yankees-react-utils';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { useOktaQuery, Stats } from '../../../data';

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
`;

const InputField = ({ label, children }) => (
    <div className="d-flex align-items-center me-4">
        <span className="me-1">{ label }</span>
        {children}
    </div>
);

const getLevelOptions = (translate, battingSeasons) => {
    const extra = [{ Abbr: translate('common.all'), LevelID: null, SortOrder: 0 }, { Abbr: translate('sections.reports.hitter.level-min'), LevelID: 7, SortOrder: 2 }];

    const levels = battingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const HitterStrengthsTrendsTopLevelFilter = ({ playerId, avgRange, setAvgRange, swingRange, setSwingRange, defaultLevelId, levels, setLevels, hasBlast }) => {
    const { t: translate } = useTranslation();
    const [innerAvgRange, setInnerAvgRange] = useState(avgRange);
    const [innerSwingRange, setInnerSwingRange] = useState(swingRange);
    const [innerLevels, setInnerLevels] = useState(levels);

    const { data: battingSeasons, isFetching: isFetchingBattingSeasons } = useOktaQuery(
        ['battingSeasons'],
        (t) => Stats.seasonBatting(t, { playerid: playerId }),
    );

    useEffect(() => {
        if (defaultLevelId && battingSeasons) {
            setLevels(getLevelOptions(translate, battingSeasons).map((lo) => ({
                ...lo,
                checked: lo.value === defaultLevelId,
            })));
        }
    }, [defaultLevelId, battingSeasons, setLevels, translate]);

    const applyFilters = () => {
        setLevels(innerLevels);
        setAvgRange(innerAvgRange);
        setSwingRange(innerSwingRange);
    };

    const testNumberInput = (value) => value === '' || /^[0-9\b]+$/.test(value);

    const handleAvgRangeInput = (value) => {
        if (testNumberInput(value)) { setInnerAvgRange(value); }
    };

    const handleSwingRangeInput = (value) => {
        if (testNumberInput(value)) { setInnerSwingRange(value); }
    };

    useEffect(() => { setInnerLevels(levels); }, [levels]);

    const onLevelDropdownChange = (o, key, e) => {
        if (key === translate('common.all') && e.target.checked) {
            setInnerLevels(innerLevels.map((l, i) => ({
                ...l,
                checked: !i,
            })));
        } else {
            const newLevels = innerLevels.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setInnerLevels(newLevels);
        }
    };

    const selectedLevels = innerLevels.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].name : `${selectedLevels.length} ${translate('baseball.level_other')}`;

    if (isFetchingBattingSeasons) {
        return <LoadingSpinner />;
    }
    return (
        <div className="border rounded py-2 px-3 d-flex align-items-center bg-white">
            <InputField label={translate('sections.reports.hitter.batting-stats-rolling-average')}>
                <Form.Control value={innerAvgRange} onChange={(e) => handleAvgRangeInput(e.target.value)} style={{ width: 55 }} />
            </InputField>
            {hasBlast && (
                <InputField label={translate('sections.reports.hitter.blast-stats-rolling-average')}>
                    <Form.Control value={innerSwingRange} onChange={(e) => handleSwingRangeInput(e.target.value)} style={{ width: 75 }} />
                </InputField>
            )}
            <InputField label={`${translate('baseball.level_one')}:`}>
                <StyledCheckboxDropdown
                    value={innerLevels}
                    onChange={onLevelDropdownChange}
                    options={innerLevels}
                    titleOverrideFormat
                    title={levelPlaceholder}
                />
            </InputField>
            <Button variant="primary" onClick={applyFilters}>{translate('common.apply')}</Button>
        </div>
    );
};

HitterStrengthsTrendsTopLevelFilter.propTypes = {
    playerId: PropTypes.number.isRequired,
    avgRange: PropTypes.string.isRequired,
    setAvgRange: PropTypes.func.isRequired,
    swingRange: PropTypes.string.isRequired,
    setSwingRange: PropTypes.func.isRequired,
    defaultLevelId: PropTypes.number,
    levels: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    })).isRequired,
    setLevels: PropTypes.func.isRequired,
    hasBlast: PropTypes.bool.isRequired,
};

HitterStrengthsTrendsTopLevelFilter.defaultProps = {
    defaultLevelId: null,
};

export default HitterStrengthsTrendsTopLevelFilter;
